import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Home, RefreshCw, Loader2, Inbox } from "lucide-react";
import { listLeads, getLeadStats } from "@/lib/api";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

export default function Admin() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [l, s] = await Promise.all([listLeads(), getLeadStats()]);
      setLeads(l);
      setStats(s);
    } catch (e) {
      setError("Couldn't load leads. Please refresh.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#F9F8F6]">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F9F8F6]/85 border-b border-[#E2DDD7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" data-testid="admin-back-home" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#D95D39] flex items-center justify-center text-white">
              <Home strokeWidth={1.75} className="w-5 h-5" />
            </div>
            <div className="leading-tight">
              <div className="font-[Outfit] font-semibold text-[#2C3D30] text-base">T&amp;R Capital — Admin</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#5B6B5F]">Lead inbox</div>
            </div>
          </Link>
          <Button
            data-testid="admin-refresh"
            onClick={load}
            disabled={loading}
            className="tr-btn-secondary rounded-full h-10 px-5"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <div className="mb-8">
          <h1 className="font-[Outfit] font-semibold text-3xl text-[#2C3D30]">Seller Leads</h1>
          <p className="text-[#5B6B5F] mt-2">Every submission from your landing page lands here.</p>
        </div>

        {stats && (
          <div className="grid sm:grid-cols-3 gap-4 mb-10">
            <StatCard label="Total Leads" value={stats.total} testid="stat-total" />
            <StatCard
              label="States Active"
              value={Object.keys(stats.by_state || {}).length}
              testid="stat-states-active"
            />
            <StatCard
              label="Top State"
              value={Object.keys(stats.by_state || {})[0] || "—"}
              testid="stat-top-state"
            />
          </div>
        )}

        <div className="bg-white border border-[#E2DDD7] rounded-2xl overflow-hidden">
          {error && (
            <div data-testid="admin-error" className="p-6 text-sm text-[#D95D39]">{error}</div>
          )}

          {loading ? (
            <div className="p-16 text-center text-[#5B6B5F] flex flex-col items-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin" />
              Loading leads...
            </div>
          ) : leads.length === 0 ? (
            <div data-testid="admin-empty" className="p-16 text-center flex flex-col items-center gap-3">
              <Inbox className="w-10 h-10 text-[#D95D39]" strokeWidth={1.5} />
              <div className="font-[Outfit] text-lg text-[#2C3D30]">No leads yet</div>
              <div className="text-sm text-[#5B6B5F] max-w-sm">
                Submitted leads from your landing page will show up here.
              </div>
            </div>
          ) : (
            <Table data-testid="admin-leads-table">
              <TableHeader>
                <TableRow className="bg-[#F1EDE7] hover:bg-[#F1EDE7]">
                  <TableHead>Submitted</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead>Timeline</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((l) => (
                  <TableRow key={l.id} data-testid={`admin-row-${l.id}`}>
                    <TableCell className="text-xs text-[#5B6B5F] whitespace-nowrap">
                      {formatDate(l.created_at)}
                    </TableCell>
                    <TableCell className="font-medium text-[#2C3D30]">{l.full_name}</TableCell>
                    <TableCell className="text-sm">
                      <div className="text-[#2C3D30]">{l.email}</div>
                      <div className="text-[#5B6B5F] text-xs">{l.phone}</div>
                    </TableCell>
                    <TableCell className="text-sm text-[#2C3D30]">{l.property_address}</TableCell>
                    <TableCell>
                      <Badge className="bg-[#F1EDE7] text-[#2C3D30] hover:bg-[#F1EDE7] border-0">
                        {l.state}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-[#5B6B5F]">{l.condition || "—"}</TableCell>
                    <TableCell className="text-sm text-[#5B6B5F]">{l.timeline || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E2DDD7] rounded-2xl p-6">
      <div className="text-xs uppercase tracking-[0.2em] text-[#5B6B5F]">{label}</div>
      <div className="mt-2 font-[Outfit] font-semibold text-3xl text-[#2C3D30]">{value}</div>
    </div>
  );
}
